/**
 * Platform detection utility
 * Detects if app is running in Capacitor (mobile) vs web browser
 * This allows us to apply mobile-specific fixes without affecting web version
 */

/**
 * Check if app is running in Capacitor (iOS/Android native app)
 * @returns {boolean} True if running in Capacitor, false if in web browser
 */
export function isCapacitor() {
  // CRITICAL: Android uses androidScheme "https" (Capacitor 6+), so protocol is https: — not a custom scheme.
  // Check Capacitor.getPlatform() first; if it returns ios/android, we're in the native app.
  if (typeof window !== 'undefined' && window.Capacitor?.getPlatform) {
    const platform = window.Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      return true; // Definitely in native app
    }
  }

  // Fallback: URL protocol (iOS uses trackio://, older Android may use custom scheme)
  if (typeof window !== 'undefined' && window.location) {
    const protocol = window.location.protocol;
    if (protocol !== 'http:' && protocol !== 'https:') {
      return true; // Custom scheme = Capacitor
    }
  }

  // Web: protocol is http/https and not ios/android from Capacitor
  return false;
}

/**
 * Check if app is running in web browser
 * @returns {boolean} True if running in web browser, false if in Capacitor
 */
export function isWeb() {
  return !isCapacitor();
}

/**
 * Get the current platform
 * @returns {'web' | 'ios' | 'android' | 'unknown'}
 */
export function getPlatform() {
  if (isCapacitor()) {
    if (typeof window !== 'undefined' && window.Capacitor?.getPlatform) {
      const platform = window.Capacitor.getPlatform();
      return platform || 'unknown';
    }
    // Fallback detection based on user agent
    if (typeof navigator !== 'undefined') {
      const ua = navigator.userAgent || navigator.vendor || '';
      if (/iPad|iPhone|iPod/.test(ua)) {
        return 'ios';
      }
      if (/Android/.test(ua)) {
        return 'android';
      }
    }
    return 'unknown';
  }
  return 'web';
}

/**
 * Mobile browser UA (iOS Safari, Android Chrome, in-app mail clients, etc.).
 * Used when the user opens an email confirmation link on the website: redirect to trackio://
 * so the native app can complete sign-in — same flow as iOS, including Android.
 */
export function isMobileBrowserForEmailAppRedirect() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod|Android/i.test(ua);
}

/**
 * Web only: macOS, iOS, or iPadOS (Sign in with Apple on the web is only offered on Apple platforms).
 */
export function isApplePlatformBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return true;
  if (/Mac OS X|Macintosh/i.test(ua) || /^Mac/i.test(platform)) return true;
  return false;
}

